<template>
  <v-dialog
    scrollable
    max-width="55%"
    persistent
    :value="show"
    @click:outside="handleClose"
  >
    <v-card>
      <v-card-title class="justify-between">
        {{ $t('organization_settings.webhooks_log_details_title') }}
      </v-card-title>

      <v-card-text>
        <v-tabs slider-size="4" slider-color="blue" v-model="selectedTab">
          <v-tab active-class="grey--text text--darken-4" key="request">{{
            $t('organization_settings.webhooks_request_title')
          }}</v-tab>
          <v-tab active-class="grey--text text--darken-4" key="response">{{
            $t('organization_settings.webhooks_response_title')
          }}</v-tab>

          <p
            class="d-flex justify-end align-center flex-grow-1 pr-8 mb-0 grey--text text--darken-2 text-right"
          >
            <v-icon class="mr-1" small>mdi-clock-outline</v-icon>
            {{ requestTime }}
          </p>

          <v-tab-item class="px-8 py-4" key="request" style="overflow-x:auto">
            <h5>{{ $t('organization_settings.webhooks_headers_title') }}</h5>
            <pre>{{ requestHeaders }}</pre>

            <h5 class="mt-8">
              {{ $t('organization_settings.webhooks_payload_title') }}
            </h5>

            <pre class="pa-0">
              <JsonViewer theme="webhook-json-viewer" copyable sort :value="requestPayload"/>
            </pre>
          </v-tab-item>

          <v-tab-item class="pa-8" key="response">
            <h5>{{ $t('organization_settings.webhooks_headers_title') }}</h5>
            <pre>{{ responseHeaders }}</pre>

            <h5 class="mt-8">
              {{ $t('organization_settings.webhooks_body_title') }}
            </h5>

            <pre class="pa-0">
              <JsonViewer theme="webhook-json-viewer" :value="responseBody" />
            </pre>
          </v-tab-item>
        </v-tabs>
      </v-card-text>

      <v-card-actions class="px-6 py-4 justify-end">
        <Button
          plain
          :text="$t('common.button.close')"
          @click="handleClose()"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import Button from '@/components/UI/Button'

export default {
  name: 'LogDetailModal',

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    logData: {
      type: Object,
    },
  },

  components: {
    JsonViewer: () =>
      import(/* webpackChunkName: "vue-json-viewer" */ 'vue-json-viewer'),
    Button,
  },

  data() {
    return {
      selectedTab: '',
    }
  },

  computed: {
    requestHeaders() {
      const headers = this.logData?.request?.headers
      return headers ? this.getHeadersText(headers) : ''
    },

    responseHeaders() {
      const headers = this.logData?.response?.headers
      return headers ? this.getHeadersText(headers) : ''
    },

    requestPayload() {
      return this.logData?.request?.body ?? {}
    },

    responseBody() {
      return this.logData?.response?.body ?? {}
    },

    requestTime() {
      const ms = this.logData?.requestTime ?? 0
      const seconds = moment.duration(ms).asSeconds()
      return this.$t('organization_settings.webhooks_request_time', { seconds })
    },
  },

  methods: {
    getHeadersText(headers) {
      return Object.keys(headers).reduce((result, key) => {
        return `${result}${key}: ${headers[key]}\n`
      }, '')
    },

    handleClose() {
      this.selectedTab = 'request'
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
.v-tabs-bar {
  position: sticky;
  top: 0px !important;
  z-index: 1;
}
.webhook-json-viewer {
  background: transparent;
  white-space: nowrap;
  color: $color-blue-grey-darken2;
  font-size: 14px;
  font-family: monospace;

  .jv-ellipsis {
    color: $color-blue-grey-darken1;
    background-color: $color-blue-grey-lighten3;
    display: inline-block;
    line-height: 0.9;
    font-size: 14px;
    padding: 0px 4px 2px 4px;
    border-radius: 3px;
    vertical-align: 2px;
    cursor: pointer;
    user-select: none;
  }
  .jv-button {
    color: $color-blue;
  }
  .jv-key {
    color: $color-blue-grey-darken3;
  }
  .jv-item {
    &.jv-array {
      color: $color-blue-grey-darken3;
    }
    &.jv-boolean {
      color: $color-red-darken1;
    }
    &.jv-function {
      color: $color-blue-grey-darken3;
    }
    &.jv-number {
      color: $color-red-darken1;
    }
    &.jv-number-float {
      color: $color-red-darken1;
    }
    &.jv-number-integer {
      color: $color-red-darken1;
    }
    &.jv-object {
      color: $color-blue-grey-darken3;
    }
    &.jv-undefined {
      color: $color-yellow;
    }
    &.jv-string {
      color: $color-green;
      word-break: break-word;
      white-space: normal;
    }
  }

  .jv-code {
    padding: 0 8px !important;

    .jv-toggle {
      &:before {
        padding: 0px 2px;
        border-radius: 2px;
      }
      &:hover {
        &:before {
          background: $color-blue-grey-lighten2;
        }
      }
    }
  }
}
</style>
